import {
  useNavigate,
  useOutletContext,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import type { HubOutletContext } from './tournament-hub/TournamentHubLayoutRoute';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { ArrowLeft } from 'lucide-react';
import { AppHeader } from '@/components/AppHeader';
import type { RequiredProfileField } from '@swiss-magic/shared-types';
import { KO_FINISH_TOP_OPTIONS } from '@swiss-magic/shared-types';
import { useTournamentFormController } from './tournament-form/useTournamentFormController';
import { GameSection } from './tournament-form/GameSection';
import {
  RegistrationToggles,
  ResultConfirmationSelect,
  PlayerListVisibilitySection,
} from './tournament-form/RegistrationVisibilitySection';
import { SaveTemplateButton } from './tournament-form/SaveTemplateButton';
import { StartFromTemplatePicker } from './tournament-form/StartFromTemplatePicker';
import { TiebreakCriteriaEditor } from '@/components/tournament-form/TiebreakCriteriaEditor';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import type { ReactNode } from 'react';

// 076 follow-up — small wrapper that gives each thematic chunk
// of the form a header. Replaces the previous flat layout that
// felt random when scrolling through it.
function FormGroup({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <section className="space-y-4 border-t pt-6 first:border-t-0 first:pt-0">
      <h2 className="text-lg font-semibold">{title}</h2>
      {children}
    </section>
  );
}

function requiredProfileFieldLabel(field: RequiredProfileField, t: TFunction): string {
  const key = `tournament_form.required_profile_fields.${field}`;
  const translated = t(key);
  return translated === key ? field : translated;
}

export function TournamentFormPage() {
  const { t } = useTranslation('director');
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Story 083 — the hub-wide God Mode header toggle unlocks editing a
  // non-draft tournament's settings. The legacy ?force=true URL param
  // still works (direct links / tests). At /tournaments/new there is no
  // hub outlet context, so useOutletContext returns null.
  const hubCtx = useOutletContext<HubOutletContext | null>();
  const isForceEdit =
    searchParams.get('force') === 'true' || hubCtx?.godMode === true;
  // Story 093 — co-directors can't edit settings; the form is read-only
  // for them regardless of status/force (the backend also blocks it).
  const isCoDirector = hubCtx?.role === 'co-director';

  const form = useTournamentFormController({
    id,
    isForceEdit,
    isCoDirector,
    navigate,
  });

  if (form.loading) {
    return (
      <div
        data-testid="tournament-form-page"
        className="flex min-h-screen items-center justify-center"
      >
        <p>{t('tournament_form.loading')}</p>
      </div>
    );
  }

  const systemLocked =
    form.isEdit &&
    ['registration_closed', 'in_progress', 'completed'].includes(form.tournamentStatus);

  const title = form.readOnly
    ? t('tournament_form.title_details')
    : form.isEdit
      ? t('tournament_form.title_edit')
      : t('tournament_form.title_create');

  // Story 083 — when nested under /tournaments/:id (id present), the
  // hub layout already provides AppHeader + back navigation. When at
  // /tournaments/new (no id), the page renders its own chrome.
  const nested = !!id;

  return (
    <div
      data-testid="tournament-form-page"
      className={nested ? 'w-full' : 'min-h-screen bg-background'}
    >
      {!nested && <AppHeader />}
      <main className={nested ? '' : 'flex justify-center px-4 py-12'}>
      <Card className={nested ? 'border-0 shadow-none w-full max-w-2xl mx-auto' : 'w-full max-w-2xl'}>
        <CardHeader>
          {!nested && (
            <Button
              variant="ghost"
              size="sm"
              className="mb-2 w-fit"
              onClick={() => navigate('/dashboard')}
            >
              <ArrowLeft className="mr-1 h-4 w-4" />
              {t('tournament_form.back_to_dashboard')}
            </Button>
          )}
          <CardTitle className="text-2xl">{title}</CardTitle>
        </CardHeader>
        <CardContent>
          <form
            data-testid="tournament-form"
            onSubmit={form.handleSubmit}
            className="space-y-6"
            noValidate
          >
            {/* Story 078 — picker only on /tournaments/new; the
                component renders nothing when the director has no
                templates yet, so the form stays uncluttered for
                first-time directors. */}
            {!form.isEdit && <StartFromTemplatePicker form={form} />}

            <fieldset disabled={form.readOnly} className="space-y-6">
              <FormGroup title={t('tournament_form.section.info')}>
                <div className="space-y-2">
                  <Label htmlFor="name">{t('tournament_form.name_label')}</Label>
                  <Input
                    id="name"
                    data-testid="name-input"
                    value={form.name}
                    onChange={(e) => form.setName(e.target.value)}
                    placeholder={t('tournament_form.name_placeholder')}
                    disabled={form.nameIsLocked}
                  />
                  {form.errors.name && (
                    <p className="text-sm text-destructive">{form.errors.name}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">{t('tournament_form.description_label')}</Label>
                  <Textarea
                    id="description"
                    data-testid="description-input"
                    value={form.description}
                    onChange={(e) => form.setDescription(e.target.value)}
                    placeholder={t('tournament_form.description_placeholder')}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="startDate">{t('tournament_form.start_date_label')}</Label>
                  <Input
                    id="startDate"
                    data-testid="start-date-input"
                    type="date"
                    value={form.startDate}
                    onChange={(e) => form.setStartDate(e.target.value)}
                  />
                </div>

                {/* Story 096 — FIDE rating-grade metadata (chess only). */}
                {form.game === 'chess' && (
                  <div
                    data-testid="fide-metadata-fields"
                    className="space-y-4 rounded-md border border-dashed p-3"
                  >
                    <div className="space-y-2">
                      <Label htmlFor="endDate">
                        {t('tournament_form.end_date_label')}
                      </Label>
                      <Input
                        id="endDate"
                        data-testid="end-date-input"
                        type="date"
                        value={form.endDate}
                        onChange={(e) => form.setEndDate(e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="federation">
                        {t('tournament_form.federation_label')}
                      </Label>
                      <Input
                        id="federation"
                        data-testid="federation-input"
                        maxLength={3}
                        value={form.federation}
                        onChange={(e) =>
                          form.setFederation(e.target.value.toUpperCase())
                        }
                        placeholder={t('tournament_form.federation_placeholder')}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="chiefArbiterName">
                        {t('tournament_form.chief_arbiter_name_label')}
                      </Label>
                      <Input
                        id="chiefArbiterName"
                        data-testid="chief-arbiter-name-input"
                        value={form.chiefArbiterName}
                        onChange={(e) =>
                          form.setChiefArbiterName(e.target.value)
                        }
                      />
                    </div>
                    <div className="grid gap-4 sm:grid-cols-2">
                      <div className="space-y-2">
                        <Label htmlFor="chiefArbiterFideId">
                          {t('tournament_form.chief_arbiter_fide_id_label')}
                        </Label>
                        <Input
                          id="chiefArbiterFideId"
                          data-testid="chief-arbiter-fide-id-input"
                          type="number"
                          value={form.chiefArbiterFideId}
                          onChange={(e) =>
                            form.setChiefArbiterFideId(e.target.value)
                          }
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="chiefArbiterTitle">
                          {t('tournament_form.chief_arbiter_title_label')}
                        </Label>
                        <Input
                          id="chiefArbiterTitle"
                          data-testid="chief-arbiter-title-input"
                          value={form.chiefArbiterTitle}
                          onChange={(e) =>
                            form.setChiefArbiterTitle(e.target.value)
                          }
                          placeholder={t(
                            'tournament_form.chief_arbiter_title_placeholder',
                          )}
                        />
                      </div>
                    </div>
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="location">{t('tournament_form.location_label')}</Label>
                  <Input
                    id="location"
                    data-testid="location-input"
                    value={form.location}
                    onChange={(e) => form.setLocation(e.target.value)}
                    placeholder={t('tournament_form.location_placeholder')}
                  />
                </div>

                {/* Story 084 — director-pasted maps URL. Renders the
                    location text as a link on the public + hub
                    surfaces when set. HTTPS-only, no third-party
                    geocoding. */}
                <div className="space-y-2">
                  <Label htmlFor="venueMapUrl">{t('tournament_form.venue_map_url_label')}</Label>
                  <Input
                    id="venueMapUrl"
                    data-testid="venue-map-url-input"
                    type="url"
                    value={form.venueMapUrl}
                    onChange={(e) => form.setVenueMapUrl(e.target.value)}
                    placeholder={t('tournament_form.venue_map_url_placeholder')}
                  />
                  <p className="text-xs text-muted-foreground">
                    {t('tournament_form.venue_map_url_hint')}
                  </p>
                  {form.errors.venueMapUrl && (
                    <p
                      data-testid="venue-map-url-error"
                      className="text-sm text-destructive"
                    >
                      {form.errors.venueMapUrl}
                    </p>
                  )}
                </div>

                {/* Story 084 — optional external URL + display label. */}
                <div className="space-y-2">
                  <Label htmlFor="externalUrl">{t('tournament_form.external_url_label')}</Label>
                  <Input
                    id="externalUrl"
                    data-testid="external-url-input"
                    type="url"
                    value={form.externalUrl}
                    onChange={(e) => form.setExternalUrl(e.target.value)}
                    placeholder={t('tournament_form.external_url_placeholder')}
                  />
                  <p className="text-xs text-muted-foreground">
                    {t('tournament_form.external_url_hint')}
                  </p>
                  {form.errors.externalUrl && (
                    <p
                      data-testid="external-url-error"
                      className="text-sm text-destructive"
                    >
                      {form.errors.externalUrl}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="externalUrlLabel">{t('tournament_form.external_url_label_label')}</Label>
                  <Input
                    id="externalUrlLabel"
                    data-testid="external-url-label-input"
                    value={form.externalUrlLabel}
                    onChange={(e) => form.setExternalUrlLabel(e.target.value)}
                    placeholder={t('tournament_form.external_url_label_placeholder')}
                    maxLength={60}
                  />
                  <p className="text-xs text-muted-foreground">
                    {t('tournament_form.external_url_label_hint')}
                  </p>
                </div>
              </FormGroup>

              <FormGroup title={t('tournament_form.section.format')}>
                <GameSection form={form} systemLocked={systemLocked} />

                {/* Story 105 — KO qualifier finish threshold (3ko/5ko only). */}
                {(form.system === '3ko' || form.system === '5ko') && (
                  <div className="space-y-2">
                    <Label htmlFor="koFinishTop">
                      {t('tournament_form.ko_finish_top_label')}
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      {t('tournament_form.ko_finish_top_description')}
                    </p>
                    <select
                      id="koFinishTop"
                      data-testid="ko-finish-top-select"
                      value={form.koFinishTop}
                      onChange={(e) =>
                        form.setKoFinishTop(
                          Number(e.target.value) as (typeof KO_FINISH_TOP_OPTIONS)[number],
                        )
                      }
                      className="flex h-10 w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm dark:bg-input/30"
                    >
                      {KO_FINISH_TOP_OPTIONS.map((n) => (
                        <option key={n} value={n}>
                          {n === 1
                            ? t('tournament_form.ko_finish_top_full')
                            : t('tournament_form.ko_finish_top_option', { n })}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Item 4 — round_robin / double_round_robin derive the
                    round count from the player count (n-1 or 2(n-1)),
                    so the manual input is meaningless. Hide it entirely
                    for those systems; GameSection already shows a note
                    explaining the auto-derived behaviour. */}
                {form.system !== 'round_robin' &&
                  form.system !== 'double_round_robin' && (
                    <div className="space-y-2">
                      <Label htmlFor="rounds">
                        {t('tournament_form.rounds_label')}
                      </Label>
                      {/* Item 3 — type="text" + inputMode="numeric" +
                          pattern + onChange filter keeps the field
                          strictly digits. Native type="number" lets
                          through "e", ".", "-", "+" depending on the
                          browser. */}
                      <Input
                        id="rounds"
                        data-testid="rounds-input"
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        value={form.rounds}
                        onChange={(e) =>
                          form.setRounds(e.target.value.replace(/[^0-9]/g, ''))
                        }
                        placeholder={t('tournament_form.rounds_placeholder')}
                      />
                      {form.errors.rounds && (
                        <p className="text-sm text-destructive">
                          {form.errors.rounds}
                        </p>
                      )}
                    </div>
                  )}

                <TiebreakCriteriaEditor
                  tiebreakCriteria={form.tiebreakCriteria}
                  availableTiebreaks={form.availableTiebreaks}
                  onAdd={form.addCriterion}
                  onRemove={form.removeCriterion}
                  onMove={form.moveCriterion}
                />
              </FormGroup>

              <FormGroup title={t('tournament_form.section.registration')}>
                <RegistrationToggles form={form} />

                <div className="space-y-2">
                  <Label htmlFor="maxPlayers">{t('tournament_form.max_players_label')}</Label>
                  <Input
                    id="maxPlayers"
                    data-testid="max-players-input"
                    type="number"
                    min="1"
                    value={form.maxPlayers}
                    onChange={(e) => form.setMaxPlayers(e.target.value)}
                    placeholder={t('tournament_form.max_players_placeholder')}
                    aria-invalid={!!form.errors.maxPlayers}
                  />
                  {form.errors.maxPlayers && (
                    <p className="text-sm text-destructive" data-testid="max-players-error">
                      {form.errors.maxPlayers}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="entryFee">{t('tournament_form.entry_fee_label')}</Label>
                  <Input
                    id="entryFee"
                    data-testid="entry-fee-input"
                    value={form.entryFee}
                    onChange={(e) => form.setEntryFee(e.target.value)}
                    placeholder={t('tournament_form.entry_fee_placeholder')}
                  />
                </div>

                {/* Story 100 — spot-secured registration flow (all games). */}
                <div className="space-y-2">
                  <Label htmlFor="spotConfirmationMode">
                    {t('tournament_form.spot_confirmation_label')}
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    {t('tournament_form.spot_confirmation_description')}
                  </p>
                  <select
                    id="spotConfirmationMode"
                    data-testid="spot-confirmation-mode-select"
                    value={form.spotConfirmationMode}
                    onChange={(e) =>
                      form.setSpotConfirmationMode(
                        e.target.value as 'manual' | 'auto',
                      )
                    }
                    className="flex h-10 w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm dark:bg-input/30"
                  >
                    <option value="manual">
                      {t('tournament_form.spot_confirmation_manual')}
                    </option>
                    <option value="auto">
                      {t('tournament_form.spot_confirmation_auto')}
                    </option>
                  </select>
                </div>

                {form.onlineRegistration && (
                  <div className="space-y-2">
                    <Label>{t('tournament_form.required_profile_fields_label')}</Label>
                    <p className="text-xs text-muted-foreground">
                      {t('tournament_form.required_profile_fields_description')}
                    </p>
                    <div
                      data-testid="required-profile-fields-section"
                      className="grid grid-cols-1 gap-1 sm:grid-cols-2"
                    >
                      {form.availableRequiredProfileFields.map((field) => (
                        <label
                          key={field}
                          className="flex items-center gap-2 text-sm"
                        >
                          <input
                            type="checkbox"
                            data-testid={`required-profile-field-${field}`}
                            checked={form.requiredProfileFields.includes(field)}
                            onChange={() => form.toggleRequiredProfileField(field)}
                          />
                          {requiredProfileFieldLabel(field, t)}
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </FormGroup>

              <FormGroup title={t('tournament_form.section.visibility')}>
                <PlayerListVisibilitySection form={form} />
                <ResultConfirmationSelect form={form} />
              </FormGroup>
            </fieldset>
            {!form.readOnly && (
              <div className="flex flex-col gap-2 sm:flex-row">
                <Button
                  type="submit"
                  data-testid="submit-tournament-button"
                  className="flex-1"
                  disabled={form.submitting}
                >
                  {form.submitting
                    ? t('tournament_form.submitting')
                    : form.isEdit
                      ? t('tournament_form.submit_update')
                      : t('tournament_form.submit_create')}
                </Button>
                <SaveTemplateButton form={form} />
              </div>
            )}
          </form>
        </CardContent>
      </Card>
      </main>
    </div>
  );
}
